/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICategorizeOptions, IFilterOptions, ITagAttribute, TreeViewDataItem } from 'store/sidebar/types';
import { ITab } from '../../api/workspace/types';
import { IAxisStyleSettings, IWorkspaceItem, MapOptions } from './types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAllWorkspaces, getWorkspaceById, R_addWorkspace, R_copyWorkspace, R_deleteWorkspace, R_editWorkspace, R_editAnalogueWorkspaceFilters, R_editWorkspaceFilters, R_editFilterOptions, saveMapOptions, savePlotOptions, processWorkspaceAccessLevel } from 'api/workspace/service';
import { addNotification } from 'store/notifications/slice';
import { BaseThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { AxiosError } from 'axios';
import { ccsSlice } from 'store/ccs/slice';
import { createTabs } from '../../api/workspace/processors';
import { handleRequestErrors } from 'helpers/functions/commons';
import { getWorkSpaceFromLS, checkAndHandleReadOnlyAccess } from 'helpers/functions/workspaceHelpers';
import { R_getProjectView } from 'api/projectView/projectViewService';
import { IUserActions } from 'api/userAction/types';
import { R_GetUserActions, R_SaveUserActions } from 'api/userAction/service';
import { ROUTES, USER_ACTION_ENUM, USER_ACTION_TYPES } from 'helpers/constants/app';
import { getPlotOptions } from 'api/sidebar/sideBarService';
import { R_changeAxisStyleSettings, R_changeStyleSettings, R_deleteStyleSettings, R_getAxisStyleSettings, R_getStyleSettings } from 'api/styleSettings/service';
import { mapViewSlice } from 'store/mapView/slice';
import { requestDeduplicationService } from 'helpers/functions/requestDeduplication';
import { workspaceSlice } from './slice';
import { T_getUserInfo } from 'store/userState/thunk';
// import { AppStore } from 'store/store';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleError = (err: AxiosError, { dispatch, rejectWithValue }: BaseThunkAPI<any, any, any, any>) => {
    if (!err.response) {
        throw err
    }
    const message = handleRequestErrors(err)
    
    if (message && message.trim().length > 0) {
        dispatch(addNotification({ type: 'error', message }))
    }
    
    return rejectWithValue(message)
}

// Access-level processing needs a valid user id. On a hard refresh (F5) the
// workspace thunks can run before T_getUserInfo has resolved, leaving the id
// empty and causing shared workspaces to be misclassified as owned/writable.
// Ensure the user is loaded before we compute access levels.
const resolveCurrentUserId = async (thunkAPI: BaseThunkAPI<any, any, any, any>): Promise<string> => {
    let currentUserId = (thunkAPI.getState() as any).userStateSlice.id;
    if (!currentUserId) {
        await thunkAPI.dispatch(T_getUserInfo());
        currentUserId = (thunkAPI.getState() as any).userStateSlice.id;
    }
    return currentUserId;
}

export const getAllWorkspacesThunk = createAsyncThunk('workspace/getAll', async ({ allTags }: { allTags: ITagAttribute[] }, thunkAPI) => {
    try {
        const state = thunkAPI.getState()
        // if(!(state as RootState)?.userStateSlice?.isSignedIn){
        //     thunkAPI.dispatch(userStateSlice.actions.setUserStatus('pending'));
        // }
        // await thunkAPI.dispatch(T_getUserInfo());
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await getAllWorkspaces(thunkAPI);

        // Get current user ID (ensuring user info is loaded first)
        const currentUserId = await resolveCurrentUserId(thunkAPI);

        // Process workspaces to extract access level for current user
        const workspaces: IWorkspaceItem[] = (response.workspaces || response).map((workspace: any) => 
            processWorkspaceAccessLevel(workspace, currentUserId)
        );
        
        const userActions = await R_GetUserActions()
        const lastActiveWorkspaceId = userActions?.workspaceId || getWorkSpaceFromLS()
        const lastActiveWorkspace = workspaces?.find((workspace: IWorkspaceItem) => workspace.id === lastActiveWorkspaceId)
        const workspaceId = lastActiveWorkspace ? lastActiveWorkspace.id : workspaces[0].id
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!(state as any).workspaceSlice.workspaceId || ((state as any).workspaceSlice.workspaceId !== workspaceId)) {
            const detailResult = await thunkAPI.dispatch(getWorkspaceByIdThunk({ id: workspaceId, allTags }));
            thunkAPI.dispatch(ccsSlice.actions.setCcsCurrentWorkSpaceId(workspaceId));

            // GetAll returns an empty sharedWith for shared recipients, so it can't
            // convey their access level. The detail endpoint does. Merge the detail's
            // access info into the list entry here, otherwise on a hard refresh the
            // detail resolves before the workspaces array exists (so getById's own
            // reducer update is skipped) and this thunk's payload overwrites it with
            // an undefined accessLevel — wrongly enabling edit controls.
            const detailWorkspace = (detailResult as any)?.payload?.workspace;
            if (detailWorkspace?.id) {
                const idx = workspaces.findIndex((w: IWorkspaceItem) => w.id === detailWorkspace.id);
                if (idx !== -1) {
                    workspaces[idx] = {
                        ...workspaces[idx],
                        isOwner: detailWorkspace.isOwner,
                        accessLevel: detailWorkspace.accessLevel,
                        sharedWith: detailWorkspace.sharedWith?.length ? detailWorkspace.sharedWith : workspaces[idx].sharedWith,
                    };
                }
            }
        }
        await thunkAPI.dispatch(T_GetUserActions(userActions));
        // thunkAPI.dispatch(userStateSlice.actions.setUserStatus(true));
        return workspaces;
    } catch (e) {
        return handleError(e as AxiosError, thunkAPI);
    }
});

export const T_GetUserActions = createAsyncThunk('workspace/getUserActions', async (userActions: IUserActions) => {
    const { actionName, entityId, action, workspaceId } = userActions
    return { actionName, entityId, action, workspaceId }
});

export const T_SaveUserActions = createAsyncThunk('workspace/saveUserActions', async (data: { selectedTabId?: string, isMapViewSelected: boolean, workspaceId: string, selectedProjectId?: string }) => {
    const path = window.location.pathname
    const pathEntries = Object.entries(ROUTES)
    const isCCs = path.includes('projects/ccs') && data?.selectedProjectId
    const pathToSave = pathEntries.find(([, value]) => path.includes(value))
    let actionName = pathToSave ? USER_ACTION_TYPES[pathToSave[0] as keyof typeof USER_ACTION_TYPES] : null
    actionName = actionName === USER_ACTION_TYPES.explore && data.isMapViewSelected ? USER_ACTION_TYPES.exploreMap : actionName || '';
    const UserActionsData: Partial<IUserActions> = {
        workspaceId: data.workspaceId,
        action: actionName ? USER_ACTION_ENUM.indexOf(actionName) : 0,
        entityId: isCCs ? data?.selectedProjectId : data.isMapViewSelected ? undefined : data?.selectedTabId
    }
    UserActionsData.workspaceId && actionName && R_SaveUserActions(UserActionsData)

    return UserActionsData
});

export const T_getProjectView = createAsyncThunk('plot/getprojectView', async (data: { workspaceId: string, plotId: string, newId?: string }, thunkAPI,) => {
    const response = await R_getProjectView(data, thunkAPI)
    return { ...response, newId: data.newId }
});

export const getWorkspaceByIdThunk = createAsyncThunk('workspace/getById', async (data: { id: string, tabId?: string, selectLast?: boolean, allTags: ITagAttribute[] }, thunkAPI) => {
    try {
        const { id, tabId, selectLast, allTags } = data
        const currentUserId = await resolveCurrentUserId(thunkAPI);

        thunkAPI.dispatch(mapViewSlice.actions.resetIsMapViewFullDataFetched(false))
        const { workspace } = await getWorkspaceById(id);
        
        // Process the workspace to extract access level
        const processedWorkspace = processWorkspaceAccessLevel(workspace, currentUserId);
        
        const { tabs }: { tabs: ITab[] } = createTabs(processedWorkspace, tabId, selectLast);
        return { workspace: processedWorkspace, tabs, allTags, tabId };
    } catch (e) {
        return handleError(e as AxiosError, thunkAPI);
    }
});

export const savePlotOptionsThunk = createAsyncThunk('workspace/savePlotOptions', async (data: { plotOptions: TreeViewDataItem | undefined, selectedTab: ITab | undefined }, thunkAPI) => {
    // Check read-only access before saving plot options
    if (data.selectedTab?.workspaceId) {
        const state = thunkAPI.getState() as any;
        const currentWorkspace = state.workspaceSlice.workspaces.find((ws: IWorkspaceItem) => ws.id === data.selectedTab?.workspaceId);
        const currentUserId = state.userStateSlice.id;
        
        if (checkAndHandleReadOnlyAccess(currentWorkspace, currentUserId, thunkAPI.dispatch)) {
            return thunkAPI.rejectWithValue('Read-only workspace');
        }
    }
    
    const response = await savePlotOptions(data, thunkAPI);
    return response;
});

export const getPlotOptionsThunk = createAsyncThunk('workspace/getPlotOptions', async (data: { plotId: string, workspaceId: string }, thunkAPI) => {
    const response = await getPlotOptions(data, thunkAPI);
    return response;
});

export const T_addWorkspace = createAsyncThunk('workspace/add', async (data: { title: string, unit: number, datasetIds: string[], sharedWith: string[] }, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as any;
        const currentUserId = state.userStateSlice.id;
        
        const response = await R_addWorkspace(data)
        
        // Process the workspace to extract access level
        return processWorkspaceAccessLevel(response, currentUserId);
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const T_copyWorkspace = createAsyncThunk('workspace/copy', async (data: { sourceWorkspaceId: string, title: string }, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as any;
        const currentUserId = state.userStateSlice.id;
        
        const response = await R_copyWorkspace(data)
        
        // Process the workspace to extract access level
        return processWorkspaceAccessLevel(response, currentUserId);
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const T_editWorkspace = createAsyncThunk('workspace/edit', async (data: Partial<IWorkspaceItem>, thunkAPI) => {
    try {
        // Check read-only access before editing
        const state = thunkAPI.getState() as any;
        const currentWorkspace = state.workspaceSlice.workspaces.find((ws: IWorkspaceItem) => ws.id === data.id);
        const currentUserId = state.userStateSlice.id;
        
        if (checkAndHandleReadOnlyAccess(currentWorkspace, currentUserId, thunkAPI.dispatch)) {
            return thunkAPI.rejectWithValue('Read-only workspace');
        }
        
        const response = await R_editWorkspace(data)
        
        // Process the workspace to extract access level
        return processWorkspaceAccessLevel(response, currentUserId);
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const T_editWorkspaceFilters = createAsyncThunk('workspace/editFilters', async (data: { id: string, title?: string, unit: number | null, plotId?: string, mapOptions?: TreeViewDataItem, filterOptions?: IFilterOptions, categorizeOptions?: ICategorizeOptions }, thunkAPI) => {
    try {
        // Check read-only access before editing filters
        const state = thunkAPI.getState() as any;
        const currentWorkspace = state.workspaceSlice.workspaces.find((ws: IWorkspaceItem) => ws.id === data.id);
        const currentUserId = state.userStateSlice.id;
        
        if (checkAndHandleReadOnlyAccess(currentWorkspace, currentUserId, thunkAPI.dispatch)) {
            return thunkAPI.rejectWithValue('Read-only workspace');
        }
        
        const requestConfig = {
            url: `/Workspace/${data.id}`,
            method: 'PATCH',
            data: {
                title: data.title,
                unit: data.unit,
                mapOptions: data.mapOptions,
                filterOptions: {
                    ...data.filterOptions,
                    filters: data.filterOptions?.filters,
                },
                categorizeOptions: data.categorizeOptions
            }
        };

        const response = await requestDeduplicationService.executeRequest(
            requestConfig,
            () => R_editWorkspaceFilters(data)
        );
        return response;
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

// Persists filter-level changes (enable/disable, clearing filters, apply toggles) via the
// dedicated, read-only-safe FilterOptions endpoint instead of the full workspace PATCH.
// No frontend read-only guard: the backend decides allow/deny for this endpoint.
export const T_editWorkspaceFilterOptions = createAsyncThunk('workspace/editFilterOptions', async (data: { id: string, filterOptions: IFilterOptions }, thunkAPI) => {
    try {
        const requestConfig = {
            url: `/Workspace/${data.id}/FilterOptions`,
            method: 'PATCH',
            data: {
                isEnabled: data.filterOptions.isEnabled,
                applyToUserData: data.filterOptions.applyToUserData,
                applyToProductionData: data.filterOptions.applyToProductionData,
                applyToBenchmarkData: data.filterOptions.applyToBenchmarkData,
                filters: data.filterOptions.filters,
            }
        };

        const response = await requestDeduplicationService.executeRequest(
            requestConfig,
            () => R_editFilterOptions(data)
        );
        return response;
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const T_editAnalogueWorkspaceFilters = createAsyncThunk('workspace/editAnalogueFilters', async (data: { id: string, title?: string, unit: number | null, plotId?: string, mapOptions?: TreeViewDataItem, filterOptions?: IFilterOptions, categorizeOptions?: ICategorizeOptions }, thunkAPI) => {
    try {
        // Check read-only access before editing analogue filters
        const state = thunkAPI.getState() as any;
        const currentWorkspace = state.workspaceSlice.workspaces.find((ws: IWorkspaceItem) => ws.id === data.id);
        const currentUserId = state.userStateSlice.id;
        
        if (checkAndHandleReadOnlyAccess(currentWorkspace, currentUserId, thunkAPI.dispatch)) {
            return thunkAPI.rejectWithValue('Read-only workspace');
        }
        
        const requestConfig = {
            url: `/Workspace/AnalogeFilterWorkspace/${data.id}`,
            method: 'PATCH',
            data: {
                title: data.title,
                unit: data.unit,
                mapOptions: data.mapOptions,
                filterOptions: {
                    ...data.filterOptions,
                    filters: data.filterOptions?.filters,
                },
                categorizeOptions: data.categorizeOptions
            }
        };

        const response = await requestDeduplicationService.executeRequest(
            requestConfig,
            () => R_editAnalogueWorkspaceFilters(data)
        );

        return response;
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const T_deleteWorkspace = createAsyncThunk('workspace/delete', async (data: { id: string }, thunkAPI) => {
    try {
        const response = await R_deleteWorkspace(data)
        return response
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const saveMapOptionsThunk = createAsyncThunk('mapOptons/saveMapOptions', async (data: { id: string, mapOptions: MapOptions }, thunkAPI) => {
    try {
        // Check read-only access before saving map options
        const state = thunkAPI.getState() as any;
        const currentWorkspace = state.workspaceSlice.workspaces.find((ws: IWorkspaceItem) => ws.id === data.id);
        const currentUserId = state.userStateSlice.id;
        
        if (checkAndHandleReadOnlyAccess(currentWorkspace, currentUserId, thunkAPI.dispatch)) {
            return thunkAPI.rejectWithValue('Read-only workspace');
        }
        
        const requestConfig = {
            url: `/Workspace/${data.id}`,
            method: 'PATCH',
            data: { mapViewOptions: data.mapOptions }
        };

        const response = await requestDeduplicationService.executeRequest(
            requestConfig,
            () => saveMapOptions(data)
        );

        return response;
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const T_getStyleSettings = createAsyncThunk('get/styleSettings', async (data: { workspaceId: string, optionType: number, identifierFieldId?: string, plotType?: number }, thunkAPI) => {
    try {
        const response = await R_getStyleSettings(data, thunkAPI)
        return response
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})
export const T_changeStyleSettings = createAsyncThunk('change/styleSettings', async (data: { workspaceId: string, styleSettings: any, optionType: number, identifierFieldId?: string }, thunkAPI) => {
    try {
        // Check read-only access before changing style settings
        const state = thunkAPI.getState() as any;
        const currentWorkspace = state.workspaceSlice.workspaces.find((ws: IWorkspaceItem) => ws.id === data.workspaceId);
        const currentUserId = state.userStateSlice.id;
        
        if (checkAndHandleReadOnlyAccess(currentWorkspace, currentUserId, thunkAPI.dispatch)) {
            return thunkAPI.rejectWithValue('Read-only workspace');
        }
        
        const response = await R_changeStyleSettings(data, thunkAPI)
        return response
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const T_getAxisStyleSettings = createAsyncThunk('get/axisStyleSettings', async (data: { workspaceId: string, optionType: number, plotType?: number, type: string }, thunkAPI) => {
    try {
        const response = await R_getAxisStyleSettings(data, thunkAPI)
        return response
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})



export const T_changeAxisStyleSettings = createAsyncThunk('change/styleAxisSettings', async (data: { workspaceId: string, styleSettings: Partial<IAxisStyleSettings>, optionType: number }, thunkAPI) => {
    try {
        // Check read-only access before changing axis style settings
        const state = thunkAPI.getState() as any;
        const currentWorkspace = state.workspaceSlice.workspaces.find((ws: IWorkspaceItem) => ws.id === data.workspaceId);
        const currentUserId = state.userStateSlice.id;
        
        if (checkAndHandleReadOnlyAccess(currentWorkspace, currentUserId, thunkAPI.dispatch)) {
            return thunkAPI.rejectWithValue('Read-only workspace');
        }
        
        const response = await R_changeAxisStyleSettings(data, thunkAPI)
        return response
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})

export const T_deleteStyleSettings = createAsyncThunk('delete/styleSettings', async (data: { workspaceId: string, optionId: string }, thunkAPI) => {
    try {
        // Check read-only access before deleting style settings
        const state = thunkAPI.getState() as any;
        const currentWorkspace = state.workspaceSlice.workspaces.find((ws: IWorkspaceItem) => ws.id === data.workspaceId);
        const currentUserId = state.userStateSlice.id;
        
        if (checkAndHandleReadOnlyAccess(currentWorkspace, currentUserId, thunkAPI.dispatch)) {
            return thunkAPI.rejectWithValue('Read-only workspace');
        }
        
        const response = await R_deleteStyleSettings(data, thunkAPI)
        return response
    } catch (error) {
        return handleError(error as AxiosError, thunkAPI);
    }
})
